import React, {
  useMemo,
  useState,
} from 'react';

import {
  Button,
  ConfigProvider,
  InputNumberProps,
  Slider,
} from 'antd';
import {
  formatUnits,
  parseUnits,
} from 'ethers';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { tokenArr } from '@/collocate';
import BalanceCom from '@/components/BalanceCom';
import Icon from '@/components/Icon';
import LpProportion from '@/components/LpProportion';
import ReturnCom from '@/components/ReturnCom';
import ScrollView from '@/components/ScrollView';
import useBestTradeExactIn from '@/hooks/useBestTradeExactIn';
import useErc20Contract from '@/hooks/useErc20Contract';
import useGetAllowanceToken from '@/hooks/useGetAllowanceToken';
import useLpBalanceOf from '@/hooks/useLpBalanceOf';
import useLpContract from '@/hooks/useLpContract';
import useLpTotalSupply from '@/hooks/useLpTotalSupply';
import useRemoveLiquidityStaticCall from '@/hooks/useRemoveLiquidity';
import useReserve from '@/hooks/useReserve';
import add from '@/image/add.svg';
import arrowKey from '@/image/arrowKey.svg';
import useTransactionSetting from '@/store/transactionSetting';
import {
  divideDecimal,
  formatTo6Decimals,
  multiplyDecimal,
  subtractDecimal,
} from '@/utils';

import InputLp from './component/InputLp';
import InputToken from './component/InputToken';

const LpInfo = () => {
    const { t } = useTranslation()
    const { transactionSetting: { mobilitySlippage, mobilityTradingHour } } = useTransactionSetting()
    const [searchParams] = useSearchParams();
    const lpAddress = searchParams.get('lpAddress') || '';
    // LP 合约实例
    const lpContract = useLpContract(lpAddress);
    // 获取我的 LP 余额
    const { balanceLp, balanceLpLod, getBalanceLp } = useLpBalanceOf(lpContract);
    // 获取lp的总量
    const { totalSupply, totalSupplyLod } = useLpTotalSupply(lpContract);
    const tokenA = useMemo(() => {
        return tokenArr[Number(searchParams.get('tokenA')) || 0];
    }, [searchParams]);
    const tokenB = useMemo(() => {
        return tokenArr[Number(searchParams.get('tokenB')) || 0];
    }, [searchParams]);
    // 获取费率
    const fromTo = useBestTradeExactIn(tokenA.address, tokenB.address, parseUnits('1')); // 获取兑换比例 hook
    const toFrom = useBestTradeExactIn(tokenB.address, tokenA.address, parseUnits('1')); // 获取兑换比例 hook
    // 获取 tokenA 和 tokenB 的储备量
    const { tokenAReserve, tokenBReserve, reserveLod, getReserve } = useReserve(lpContract, tokenA.address, tokenB.address);
    const [inputValue, setInputValue] = useState<string | number>(0); // 滑动的值
    const [lpInput, setLpInput] = useState('') // lp输入框的值
    // 获取 remove lp 对应的 tokenA 和 tokenB 的数量
    const { removeLiquidityLod,
        removeLiquidity } = useRemoveLiquidityStaticCall(() => {
            getBalanceLp()
            fromTo.getBestTradePath()
            toFrom.getBestTradePath()
            getReserve()
            getAllowance()
            setInputValue('0')
            setLpInput('')
        })
    const lpContractSing = useErc20Contract(lpAddress)
    // 授权
    const { allowanceLod, allowance, approve, allowanceLodFun, getAllowance } = useGetAllowanceToken(lpContractSing)
    const removeLiquidityFun = () => {
        // 计算滑点比例（小数）
        const slippage = divideDecimal(mobilitySlippage, 100);
        const amountAMin = parseUnits(formatTo6Decimals(multiplyDecimal(removeLpInfo.token0Amount, subtractDecimal(1, slippage))));
        const amountBMin = parseUnits(formatTo6Decimals(multiplyDecimal(removeLpInfo.token1Amount, subtractDecimal(1, slippage))));
        const deadline = parseUnits(multiplyDecimal(mobilityTradingHour, 60));
        removeLiquidity(tokenA.address, tokenB.address, parseUnits(lpInput), amountAMin, amountBMin, deadline, {
            success: t('lpInfo.removeSuccess'), error: t('lpInfo.removeFailed')
        })
    }
    const approveFun = () => {
        approve({ success: t('lpInfo.approvalSuccess'), error: t('lpInfo.approvalFailed') })
    }
    // 处理滑动条的变化
    const onChange: InputNumberProps['onChange'] = (newValue) => {
        setInputValue(newValue as number);
        if (balanceLp) {
            const input = Number(newValue) / 100
            console.log('1', input);
            const data = formatTo6Decimals(multiplyDecimal(formatUnits(balanceLp), input), 18)
            console.log('2', data);
            setLpInput(Number(data) ? data : '')
        }
    };
    const setLpInputFun = (value: string) => {
        setLpInput(value)
        const data = divideDecimal(value || 0, formatUnits(balanceLp))
        if (Number(data)) {
            setInputValue(multiplyDecimal(data, 100));
        } else {
            setInputValue(0)
        }
    }
    const myLpInfo = useMemo(() => {
        if (!balanceLp || !totalSupply) return {
            shareBf: '0',
            token0Amount: '0',
            token1Amount: '0',
        }
        const share = divideDecimal(formatUnits(balanceLp), formatUnits(totalSupply))
        const shareBf = multiplyDecimal(share, 100);
        const token0Amount = multiplyDecimal(formatUnits(tokenAReserve), share);
        const token1Amount = multiplyDecimal(formatUnits(tokenBReserve), share);
        return {
            shareBf,
            token0Amount,
            token1Amount,
        }
    }, [tokenAReserve, tokenBReserve, totalSupply, balanceLp]);
    const removeLpInfo = useMemo(() => {
        if (!lpInput || !totalSupply) return {
            shareBf: '0',
            token0Amount: '0',
            token1Amount: '0',
        }
        const share = divideDecimal(lpInput, formatUnits(totalSupply))
        const shareBf = multiplyDecimal(share, 100);
        const token0Amount = multiplyDecimal(formatUnits(tokenAReserve), share);
        const token1Amount = multiplyDecimal(formatUnits(tokenBReserve), share);
        return {
            shareBf,
            token0Amount,
            token1Amount,
        }
    }, [lpInput, totalSupply, tokenAReserve, tokenBReserve]);
    const isShow = useMemo(() => {
        if (!lpInput) return {
            isApprove: true,
            isRem: true
        }
        const isRem = allowance >= parseUnits(lpInput)
        return {
            isApprove: isRem,
            isRem: !isRem
        }
    }, [allowance, lpInput])
    return (
        <ConfigProvider
            theme={{
                components: {
                    InputNumber: {
                        inputFontSizeLG: 26,
                        paddingBlockLG: 6,
                        paddingInlineLG: 0,
                    },
                },
            }}>
            <div className='py-2.5 mb-7.5 pl-4 md:hidden ' > <ReturnCom title={`LP${t('lpInfo.details')}`} /></div>
            <ScrollView className='px-4 text-mainBlack text-sm md:pb-10 md:max-w-xl md:mx-auto md:mt-10' px={'100px'}>
                <LpProportion type={false} tokenA={tokenA} tokenB={tokenB} myLpInfo={myLpInfo} balanceLp={balanceLp} balanceLpLod={balanceLpLod} loading={totalSupplyLod || reserveLod || balanceLpLod} />
                <div className='mt-5 bg-white rounded-5 p-3.75  border border-ee mb-2.5'>
                    <p className='text-aa pb-3.75'>{t('lpInfo.amount')}</p>
                    <p className='text-6.5 pb-2.5'>{formatTo6Decimals(inputValue)}%</p>
                    <Slider value={inputValue as number}
                        className='mb-2.5'
                        onChange={onChange} defaultValue={0} />
                    <div className='flex items-center justify-between '>
                        {[25, 50, 75, 100].map((item) => (
                            <div onClick={() => onChange(item)} className='cursor-pointer flex items-center justify-center border border-ee rounded-5 w-14.5 h-7.5' key={item}>
                                {item === 100 ? 'MAX' : `${item}%`}
                            </div>
                        ))}
                    </div>
                </div>
                <div>
                    <div className='relative  mb-5'>
                        <InputLp value={lpInput} setAmount={setLpInputFun} tokenBalance={balanceLp} tokenBalanceLod={balanceLpLod} fromTokenLogo={tokenA.logo} toTokenLogo={tokenB.logo} fromSymbol={tokenA.symbol} toSymbol={tokenB.symbol} />
                        <div className="w-7.5 h-7.5 bg-f7 rounded-full flex items-center justify-center absolute left-1/2 -bottom-2 transform -translate-x-1/2 translate-y-1/2">
                            <Icon className="w-4 cursor-pointer" src={arrowKey} />
                        </div>
                    </div>
                </div>
                <div className='relative  mb-5'>
                    <InputToken value={removeLpInfo.token0Amount} logo={tokenA.logo} symbol={tokenA.symbol} />
                    <div className="w-7.5 h-7.5 bg-f7 rounded-full flex items-center justify-center absolute left-1/2 -bottom-2 transform -translate-x-1/2 translate-y-1/2">
                        <Icon className="w-4 cursor-pointer" src={add} />
                    </div>
                </div>
                <InputToken value={removeLpInfo.token1Amount} logo={tokenB.logo} symbol={tokenB.symbol} />
                <div className='text-a8 text-sm mt-2.5 mb-8'>
                    <div className='flex items-start justify-between mb-2.5'>
                        <div>{t('lpInfo.price')}</div>
                        <div className='text-right'>
                            <div className='flex items-center'>1 {tokenB.symbol} = &nbsp; <BalanceCom balance={toFrom.bestTrade.amountOut} text={` ${tokenA.symbol}`} loading={toFrom.bestTradeExactInLod} /></div>
                            <div className='flex items-center'>1 {tokenA.symbol} =  &nbsp; <BalanceCom balance={fromTo.bestTrade.amountOut} text={` ${tokenB.symbol}`} loading={fromTo.bestTradeExactInLod} /></div>
                        </div>
                    </div>
                    <div className='flex items-center justify-between mb-2.5' >
                        <div>{t('lpInfo.slippageTolerance')}</div>
                        <div className='py-1.25 px-3.75 font-medium bg-white rounded-5  border border-ee text-00b'>
                            {mobilitySlippage}%
                        </div>
                    </div>
                    <div className='flex items-center justify-between'>
                        <div>{t('lpInfo.transactionDeadline')}</div>
                        <div className='py-1.25 px-3.75 font-medium bg-white rounded-5  border border-ee text-00b'>
                            {mobilityTradingHour}{t('lpInfo.minutes')}
                        </div>
                    </div>
                </div>
                <div className='flex items-center justify-between'>
                    <Button onClick={approveFun} disabled={isShow.isApprove} loading={allowanceLod || allowanceLodFun} className={`w-full mr-6 ${isShow.isApprove ? 'bg-b7e' : 'bg-00b  '}  rounded-10`}>{t('lpInfo.approve')}</Button>
                    <Button onClick={removeLiquidityFun} loading={removeLiquidityLod} disabled={isShow.isRem} className={`w-full mr-6 ${isShow.isRem ? 'bg-b7e' : 'bg-00b  '}  rounded-10`}>{t('lpInfo.remove')}</Button>
                </div>
            </ScrollView >
        </ConfigProvider>

    )
}

export default LpInfo